/**
 * Cost Breakdown v2 — shared UI kit.
 *
 * Every CB sheet is built from the same pieces so the whole module looks and
 * behaves consistently:
 *   fmt()               value formatting (currency / number / percent)
 *   useToast()          bottom-right toast
 *   SheetShell          gradient header + KPI cards + toolbar + body
 *   KpiCards            stat card row
 *   EditableTable       inline-editable data grid (single-click to edit)
 *   HelpModal           standard help modal with 🎧 audio ListenButton
 *   LibraryPickerModal  pick rows from a shared library and copy them into the estimate
 *   ConfirmButton       delete with built-in confirm
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { ListenButton } from '../../common/AudioGuide';
import HelpWithVideoTour, { hasHelpTour } from '../../Help/HelpWithVideoTour';
import { useCbApi } from './cbApi';

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export const fmt = (val, type) => {
  if (val === null || val === undefined || val === '') return '';
  if (type === 'currency') {
    const n = Number(val);
    return isNaN(n) ? val : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
  }
  if (type === 'percent') {
    const n = Number(val);
    if (isNaN(n)) return val;
    return n === 0 ? '' : `${(n <= 1 ? n * 100 : n).toFixed(1)}%`;
  }
  if (type === 'number') {
    const n = Number(val);
    return isNaN(n) ? val : n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }
  return val;
};

export const isNumericType = (type) => type === 'number' || type === 'currency' || type === 'percent';

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

export function useToast() {
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);
  const ToastEl = toast ? (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, padding: '12px 24px',
      background: toast.type === 'error' ? '#dc2626' : '#059669', color: '#fff',
      borderRadius: 10, fontSize: 14, fontWeight: 500, zIndex: 10000,
      boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
    }}>
      {toast.type === 'error' ? '❌' : '✅'} {toast.msg}
    </div>
  ) : null;
  return { showToast, ToastEl };
}

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

export const HeaderButton = ({ onClick, children, title }) => (
  <button onClick={onClick} title={title} style={{
    padding: '8px 16px', background: 'rgba(255,255,255,0.2)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.35)', borderRadius: 8, cursor: 'pointer',
    fontSize: 13, fontWeight: 500, backdropFilter: 'blur(4px)', whiteSpace: 'nowrap',
  }}>{children}</button>
);

export const ActionButton = ({ onClick, color = '#2563eb', children, disabled }) => (
  <button onClick={onClick} disabled={disabled} style={{
    padding: '8px 16px', background: disabled ? '#9ca3af' : color, color: '#fff',
    border: 'none', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap',
  }}>{children}</button>
);

export const ConfirmButton = ({ onConfirm, label, confirmLabel, color = '#dc2626' }) => (
  <ActionButton color={color} onClick={() => { if (window.confirm(confirmLabel || 'Are you sure?')) onConfirm(); }}>
    {label}
  </ActionButton>
);

// ---------------------------------------------------------------------------
// KPI cards
// ---------------------------------------------------------------------------

export const KpiCards = ({ cards }) => (
  <div style={{ display: 'flex', gap: 16, padding: '20px 32px 0', flexWrap: 'wrap', flexShrink: 0 }}>
    {cards.map((card) => (
      <div key={card.label} style={{
        flex: '1 1 170px', background: '#fff', borderRadius: 12,
        padding: '14px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        borderLeft: `4px solid ${card.color || '#2563eb'}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 17 }}>{card.icon}</span>
          <span style={{ fontSize: 11.5, color: '#6b7280', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>{card.label}</span>
        </div>
        <div style={{ fontSize: 21, fontWeight: 700, color: card.color || '#2563eb' }}>{card.value}</div>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Sheet shell: header + KPIs + toolbar + scrollable body
// ---------------------------------------------------------------------------

export const SheetShell = ({ title, subtitle, gradient, headerActions, kpiCards, toolbar, children, banner }) => (
  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#f3f4f6' }}>
    <div style={{
      background: gradient || 'linear-gradient(135deg, #1e293b, #334155)', color: '#fff',
      padding: '20px 32px', flexShrink: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 25, fontWeight: 700, letterSpacing: -0.5 }}>{title}</h1>
          {subtitle && <p style={{ margin: '4px 0 0', fontSize: 13.5, opacity: 0.85 }}>{subtitle}</p>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{headerActions}</div>
      </div>
    </div>
    {banner}
    {kpiCards && kpiCards.length > 0 && <KpiCards cards={kpiCards} />}
    {toolbar && <div style={{ padding: '14px 32px 0', flexShrink: 0 }}>{toolbar}</div>}
    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 32px 32px' }}>{children}</div>
  </div>
);

export const LoadingState = ({ label = 'Loading…' }) => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 44, marginBottom: 12 }}>📊</div>
      <p style={{ color: '#6b7280', fontSize: 15 }}>{label}</p>
    </div>
  </div>
);

export const ErrorState = ({ error, onRetry }) => (
  <div style={{ padding: 48, textAlign: 'center' }}>
    <div style={{ fontSize: 44, marginBottom: 12 }}>❌</div>
    <h3 style={{ color: '#dc2626', margin: '0 0 6px' }}>Something went wrong</h3>
    <p style={{ color: '#6b7280' }}>{error}</p>
    {onRetry && (
      <button onClick={onRetry} style={{ marginTop: 12, padding: '8px 24px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
        Retry
      </button>
    )}
  </div>
);

export const EmptyState = ({ icon = '📋', title, children }) => (
  <div style={{ textAlign: 'center', padding: '56px 24px', color: '#6b7280' }}>
    <div style={{ fontSize: 56, marginBottom: 14 }}>{icon}</div>
    <h2 style={{ marginBottom: 8, color: '#374151' }}>{title}</h2>
    <div style={{ fontSize: 14, lineHeight: 1.6 }}>{children}</div>
  </div>
);

export const NoEstimateState = ({ section }) => (
  <EmptyState icon="📋" title="No Estimate Selected">
    <p>
      Pick an estimate from the <strong>Estimates dropdown</strong> in the top bar, or open one from the{' '}
      <strong>Estimate Log</strong>, to work on its {section || 'Cost Breakdown'}.
    </p>
  </EmptyState>
);

// ---------------------------------------------------------------------------
// Editable table (single-click a cell to edit; computed columns are read-only)
// ---------------------------------------------------------------------------

export const EditableTable = ({
  columns,             // [{ key, label, type, width, computed }]
  rows,
  onCellSave,          // async (row, key, coercedValue)
  selectedRows,        // Set of ids (optional -> checkbox column)
  onToggleRow,
  onToggleAll,
  accent = '#2563eb',
  groupedHeader = null, // optional [{ label, color, span }] rendered above the column header row
  rowStyle = null,      // optional (row, idx) => style overrides
}) => {
  const [editing, setEditing] = useState(null); // { id, key }
  const [editValue, setEditValue] = useState('');

  const startEdit = (row, col) => {
    if (col.computed) return;
    setEditing({ id: row.id, key: col.key });
    setEditValue(String(row[col.key] ?? ''));
  };

  const commit = async (row, col) => {
    const original = String(row[col.key] ?? '');
    setEditing(null);
    if (editValue === original) return;
    let val = editValue === '' ? null : editValue;
    if (val !== null && isNumericType(col.type)) {
      const n = Number(val);
      val = Number.isFinite(n) ? n : null;
    }
    await onCellSave(row, col.key, val);
  };

  const hasSelection = !!(selectedRows && onToggleRow);

  return (
    <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
        <thead>
          {groupedHeader && (
            <tr>
              {hasSelection && <th style={{ borderBottom: '1px solid #e2e8f0' }} />}
              {groupedHeader.map((g, i) => (
                <th key={i} colSpan={g.span} style={{
                  padding: '7px 10px', background: g.color, color: '#fff', fontSize: 11.5,
                  fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4,
                  borderRight: '2px solid #fff', whiteSpace: 'nowrap',
                }}>{g.label}</th>
              ))}
            </tr>
          )}
          <tr style={{ background: '#f8fafc' }}>
            {hasSelection && (
              <th style={{ width: 38, padding: '8px 6px', borderBottom: '2px solid #e2e8f0', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={rows.length > 0 && rows.every((r) => selectedRows.has(r.id))}
                  onChange={onToggleAll}
                />
              </th>
            )}
            {columns.map((col) => (
              <th key={col.key} title={col.computed ? 'Calculated automatically' : undefined} style={{
                padding: '8px 10px', borderBottom: '2px solid #e2e8f0',
                textAlign: isNumericType(col.type) ? 'right' : 'left',
                fontWeight: 600, color: col.computed ? '#7c3aed' : '#374151', fontSize: 11.5,
                whiteSpace: 'nowrap', minWidth: col.width,
              }}>
                {col.label}{col.computed ? ' ⚡' : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.id} style={{
              background: idx % 2 === 0 ? '#fff' : '#fafbfc',
              ...(rowStyle ? rowStyle(row, idx) : null),
            }}>
              {hasSelection && (
                <td style={{ padding: 6, textAlign: 'center', borderBottom: '1px solid #f1f5f9' }}>
                  <input type="checkbox" checked={selectedRows.has(row.id)} onChange={() => onToggleRow(row.id)} />
                </td>
              )}
              {columns.map((col) => {
                const isEditing = editing && editing.id === row.id && editing.key === col.key;
                return (
                  <td
                    key={col.key}
                    onClick={() => { if (!isEditing) startEdit(row, col); }}
                    style={{
                      padding: '6px 10px', borderBottom: '1px solid #f1f5f9',
                      textAlign: isNumericType(col.type) ? 'right' : 'left',
                      whiteSpace: 'nowrap', maxWidth: (col.width || 140) + 60,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      cursor: col.computed ? 'default' : 'pointer',
                      color: col.computed ? '#7c3aed' : '#1f2937',
                      fontStyle: col.computed ? 'italic' : 'normal',
                    }}
                  >
                    {isEditing ? (
                      <input
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => commit(row, col)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commit(row, col);
                          if (e.key === 'Escape') setEditing(null);
                        }}
                        style={{
                          width: '100%', padding: '2px 6px', border: `2px solid ${accent}`,
                          borderRadius: 4, fontSize: 12, outline: 'none',
                          textAlign: isNumericType(col.type) ? 'right' : 'left',
                        }}
                      />
                    ) : (
                      fmt(row[col.key], col.type)
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Help modal (standard: title + audio ListenButton + rich content)
// ---------------------------------------------------------------------------

export const HelpModal = ({ isOpen, onClose, title, accent = '#2563eb', guideId, children }) => {
  if (!isOpen) return null;
  const tourAvailable = hasHelpTour(guideId);
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, padding: 0,
        maxWidth: tourAvailable ? 900 : 680, width: '92%',
        maxHeight: '82vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 24px', background: accent, color: '#fff', flexShrink: 0,
        }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>❓ {title}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} data-audio-skip>
            <ListenButton compact guideId={guideId} title={title} accent={accent} dark />
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#fff' }}>×</button>
          </div>
        </div>
        <div style={{
          padding: 24, overflowY: 'auto', fontSize: 14, lineHeight: 1.7, color: '#374151',
          flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        }}>
          <HelpWithVideoTour guideId={guideId} title={title} accent={accent} defaultTab="guide" bleed={24}>
            {children}
          </HelpWithVideoTour>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Library picker: browse shared library rows, select, copy into the estimate
// ---------------------------------------------------------------------------

export const LibraryPickerModal = ({
  isOpen,
  onClose,
  onImported,          // (count) => void
  resource,            // e.g. 'indirects' -> /cost-breakdown/indirects
  estimateId,
  title,
  accent = '#2563eb',
  displayColumns,      // [{ key, label, type }] shown in the picker table
  filterColumn = null, // optional column to offer a quick filter dropdown on
}) => {
  const api = useCbApi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;
    setLoading(true);
    setSelected(new Set());
    api.get(`/cost-breakdown/${resource}?library=1`)
      .then((res) => { if (mounted) { setRows(res.data?.data || []); setError(null); } })
      .catch((err) => { if (mounted) setError(err.message); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [isOpen, resource, api]);

  const filterOptions = useMemo(() => {
    if (!filterColumn) return [];
    return [...new Set(rows.map((r) => r[filterColumn]).filter(Boolean))].sort();
  }, [rows, filterColumn]);

  const visible = useMemo(() => {
    let out = rows;
    if (filterColumn && filterValue) out = out.filter((r) => r[filterColumn] === filterValue);
    if (search) {
      const term = search.toLowerCase();
      out = out.filter((r) => displayColumns.some((c) => String(r[c.key] ?? '').toLowerCase().includes(term)));
    }
    return out;
  }, [rows, search, filterValue, filterColumn, displayColumns]);

  const doImport = async () => {
    if (selected.size === 0) return;
    setImporting(true);
    try {
      const res = await api.post(`/cost-breakdown/${resource}/import-from-library`, {
        estimate_id: estimateId,
        ids: [...selected],
      });
      onImported(res.data?.imported || selected.size);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, maxWidth: 900, width: '94%', maxHeight: '84vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ padding: '16px 24px', background: accent, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>📚 {title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#fff' }}>×</button>
        </div>

        <div style={{ padding: '14px 24px', display: 'flex', gap: 10, alignItems: 'center', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
          <input
            placeholder="Search library…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: '1 1 220px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}
          />
          {filterColumn && filterOptions.length > 0 && (
            <select value={filterValue} onChange={(e) => setFilterValue(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}>
              <option value="">All {filterColumn.replace(/_/g, ' ')}</option>
              {filterOptions.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          )}
          <span style={{ fontSize: 12.5, color: '#6b7280' }}>{selected.size} selected of {visible.length}</span>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
          {loading ? <LoadingState label="Loading library…" /> : error ? <ErrorState error={error} /> : visible.length === 0 ? (
            <EmptyState icon="📚" title="Library is empty">
              <p>No library rows found. Library rows are shared reference data available to every estimate.</p>
            </EmptyState>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                  <th style={{ width: 38, padding: 8, textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>
                    <input
                      type="checkbox"
                      checked={visible.length > 0 && visible.every((r) => selected.has(r.id))}
                      onChange={() => {
                        const all = visible.every((r) => selected.has(r.id));
                        setSelected((prev) => {
                          const next = new Set(prev);
                          visible.forEach((r) => (all ? next.delete(r.id) : next.add(r.id)));
                          return next;
                        });
                      }}
                    />
                  </th>
                  {displayColumns.map((c) => (
                    <th key={c.key} style={{ padding: '8px 10px', borderBottom: '2px solid #e2e8f0', textAlign: isNumericType(c.type) ? 'right' : 'left', fontWeight: 600, fontSize: 11.5, whiteSpace: 'nowrap' }}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((row, idx) => (
                  <tr
                    key={row.id}
                    onClick={() => setSelected((prev) => {
                      const next = new Set(prev);
                      next.has(row.id) ? next.delete(row.id) : next.add(row.id);
                      return next;
                    })}
                    style={{ cursor: 'pointer', background: selected.has(row.id) ? '#eff6ff' : idx % 2 === 0 ? '#fff' : '#fafbfc' }}
                  >
                    <td style={{ padding: 6, textAlign: 'center', borderBottom: '1px solid #f1f5f9' }}>
                      <input type="checkbox" checked={selected.has(row.id)} readOnly />
                    </td>
                    {displayColumns.map((c) => (
                      <td key={c.key} style={{ padding: '6px 10px', borderBottom: '1px solid #f1f5f9', textAlign: isNumericType(c.type) ? 'right' : 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}>
                        {fmt(row[c.key], c.type)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          <ActionButton color={accent} onClick={doImport} disabled={selected.size === 0 || importing}>
            {importing ? 'Importing…' : `Import ${selected.size} row${selected.size === 1 ? '' : 's'} into estimate`}
          </ActionButton>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Export helper (blob download)
// ---------------------------------------------------------------------------

export async function downloadExport(api, path, filename) {
  const response = await api.get(path, { responseType: 'blob' });
  const url = URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
